import { AcademicYear } from '@/types/academicYears';
import type { AcademicYearMonth } from '@/types/paymentRequests';
import { isWithinInterval, endOfDay } from 'date-fns';
import { safeParseDate } from './paymentUtils';

// Maps month names to their calendar month numbers (1-12)
const monthToNumber: Record<AcademicYearMonth, number> = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,
};

/**
 * Formats a month with the correct 2-digit year based on academic year.
 * July-December use the start year, January-June use the end year.
 * Example: For 2025/2026 academic year, "july" -> "July '25", "january" -> "January '26"
 * Long format: "July 2025"
 * Short format: "July '25"
 */
export const formatAcademicYearMonth = (month: AcademicYearMonth, academicYear: AcademicYear, format: 'short' | 'long' = 'short'): string => {
  const startYear = new Date(academicYear.startDate).getFullYear();
  const endYear = new Date(academicYear.endDate).getFullYear();

  // July-December use start year, January-June use end year
  const monthNum = monthToNumber[month];
  const year = monthNum >= 7 ? startYear : endYear;
  if (format === 'short') {
    return `${month.charAt(0).toUpperCase() + month.slice(1)} '${year.toString().slice(-2)}`;
  }
  return `${month.charAt(0).toUpperCase() + month.slice(1)} ${year}`;
};

/**
 * Formats a month key for display. Handles both YYYY-MM keys (from the months API)
 * and academic-year month name keys ('july', 'august', …).
 *
 * @param key The month key to format
 * @param options.academicYear Defaults to `undefined`
 * @param options.locale Defaults to `en-US`
 */
export const formatMonthKey = (
  key: string,
  options: {
    academicYear?: AcademicYear;
    locale?: string;
  } = {},
): string => {
  const { academicYear, locale = 'en-US' } = options;

  if (/^\d{4}-\d{2}$/.test(key)) {
    const [yearStr, monthStr] = key.split('-');
    const date = new Date(Number(yearStr), Number(monthStr) - 1);
    const monthName = date.toLocaleDateString(locale, { month: 'long' });
    return `${monthName} ${yearStr}`;
  }
  if (academicYear) {
    return formatAcademicYearMonth(key as AcademicYearMonth, academicYear, 'long');
  }
  return key.charAt(0).toUpperCase() + key.slice(1);
};

export const getCurrentAcademicYear = (academicYears: AcademicYear[]) => {
  return academicYears.find((y) => {
    const startDate = safeParseDate(y.startDate);
    const endDate = safeParseDate(y.endDate);

    if (!startDate || !endDate) {
      return false;
    }

    return isWithinInterval(new Date(), {
      start: startDate,
      end: endOfDay(endDate),
    });
  }) ?? null;
};
